import {
  IconButton,
  IconButtonGroup,
  RedoIcon,
  Tooltip,
  UndoIcon,
} from '@frontend/legacy-components';

import { redoShortcutLabel, undoShortcutLabel } from '../utils/shortcutLabels';

export type DxfUndoRedoGroupProps = {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  /** Both buttons are inert while the file is locked. */
  isDisabled?: boolean;
};

/**
 * Undo and redo for mapping edits, beside the tool strips in the mapping toolbar.
 * Presentational: the session's history store owns what can be stepped.
 */
export const DxfUndoRedoGroup = ({
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  isDisabled = false,
}: DxfUndoRedoGroupProps) => {
  const isUndoDisabled = isDisabled || !canUndo;
  const isRedoDisabled = isDisabled || !canRedo;

  return (
    <IconButtonGroup
      size="md"
      borderWidth="1px"
      borderColor="separator.divider"
      borderStyle="solid"
      borderRadius="md"
    >
      {/* A disabled button swallows hover, so the tooltip wraps it while inert. */}
      <Tooltip
        placement="bottom"
        label={canUndo ? `Undo (${undoShortcutLabel()})` : 'Nothing to undo'}
        shouldWrapChildren={isUndoDisabled}
      >
        <IconButton
          aria-label="Undo"
          isDisabled={isUndoDisabled}
          onClick={onUndo}
          px={1}
          m={0}
          borderRadius="3px"
        >
          <UndoIcon boxSize={3} color="inherit" />
        </IconButton>
      </Tooltip>
      <Tooltip
        placement="bottom"
        label={canRedo ? `Redo (${redoShortcutLabel()})` : 'Nothing to redo'}
        shouldWrapChildren={isRedoDisabled}
      >
        <IconButton
          aria-label="Redo"
          isDisabled={isRedoDisabled}
          onClick={onRedo}
          px={1}
          m={0}
          borderRadius="3px"
        >
          <RedoIcon boxSize={3} color="inherit" />
        </IconButton>
      </Tooltip>
    </IconButtonGroup>
  );
};
DxfUndoRedoGroup.displayName = 'DxfUndoRedoGroup';
